// =============================================
// Session Management
// Handles auth persistence across tabs & refreshes
//
// How it works:
//   LOGIN  → saves user to localStorage + sets akt_session cookie
//   LOAD   → if cookie exists, restores user from localStorage
//   LOGOUT → clears both localStorage + cookie
//   NEW TAB → localStorage is shared, cookie is shared → auto-authenticated
//   REFRESH → same as LOAD → stays logged in
//   COOKIE DELETED → cookie check fails → force re-login
//   CROSS-TAB LOGOUT → storage event listener detects removal → all tabs log out
//
// When backend is built:
//   Replace cookie with httpOnly session cookie set by server
//   Replace localStorage user with JWT token validation
// =============================================

const SESSION_STORAGE_KEY = 'akt_session_user';
const SESSION_COOKIE_NAME = 'akt_session';
const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days in seconds

// =============================================
// Cookie helpers
// =============================================

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; path=/; max-age=0`;
}

// =============================================
// Session data interface
// =============================================

export interface SessionUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;    // UserRole string
  createdAt: string;
}

// =============================================
// Public API
// =============================================

/** Save user session on login — writes to both localStorage and cookie */
export function saveSession(user: SessionUser): void {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
  setCookie(SESSION_COOKIE_NAME, user.id, SESSION_COOKIE_MAX_AGE);
}

/** Restore session on page load / new tab. Returns null if not authenticated. */
export function restoreSession(): SessionUser | null {
  // Gate: cookie must exist
  const cookieValue = getCookie(SESSION_COOKIE_NAME);
  if (!cookieValue) {
    // Cookie was deleted or expired — clear any stale localStorage
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }

  // Restore user data from localStorage
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const user: SessionUser = JSON.parse(stored);
      // Verify cookie matches stored user
      if (user.id === cookieValue) {
        return user;
      }
    }
  } catch {
    // Corrupted data — clear it
  }

  // Cookie exists but localStorage doesn't match — clear both
  clearSession();
  return null;
}

/** Clear session on logout */
export function clearSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
  deleteCookie(SESSION_COOKIE_NAME);
}

/** Check if a session cookie exists (quick check without full restore) */
export function hasSessionCookie(): boolean {
  return getCookie(SESSION_COOKIE_NAME) !== null;
}

/**
 * Listen for cross-tab logout events.
 * When another tab calls clearSession(), the storage event fires here.
 * Returns a cleanup function to remove the listener.
 */
export function onCrossTabLogout(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === SESSION_STORAGE_KEY && e.newValue === null) {
      // Another tab removed the session → log out this tab too
      callback();
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
